import React, { useEffect, useState } from 'react'
import { all_routes } from '../../router/all_routes'
import { Link, useNavigate, useParams } from 'react-router-dom'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import InstructorSidebar from '../common/instructorSidebar'
import SettingsModal from '../../../core/common/modal/settingsModal'
import { useForm } from 'react-hook-form'
import {
  getResetPayloadFromConfig,
  UserEditFormValues,
  userEditSchema,
  userFields,
} from '../../../config/fields/user'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  deleteUser,
  getUserDetail,
  putUserUpdate,
  UserDetailResponse,
} from '../../../api/user'
import { Role_Option } from '../../../core/common/selectOption/json/selectOption'
import { Flex, Select, Spin } from 'antd'
import { useNotify } from '../../../provider/NotificationProvider'

const defaultValues: UserEditFormValues = {
  fullname: '',
  email: '',
  phone_number: '',
  role: '',
  nik: '',
  address: '',
  profession: '',
  bio: '',
  profile_picture: null,
  password: '',
}

const UserEdit = () => {
  const [preview, setPreview] = useState<string | null>(null)

  const { id } = useParams<{ id: string }>()
  const notify = useNotify()
  const navigate = useNavigate()

  const responseQuery = useQuery<UserDetailResponse>({
    queryKey: ['admin-users', id],
    queryFn: () => getUserDetail(id || '', true),
    enabled: !!id,
  })
  const userDetail = responseQuery?.data?.user

  const {
    reset,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UserEditFormValues>({
    resolver: yupResolver(userEditSchema) as any,
    mode: 'onBlur',
    defaultValues,
  })

  useEffect(() => {
    if (userDetail) {
      const values = getResetPayloadFromConfig(userDetail)
      reset(values)
    }
  }, [userDetail, reset])

  const updateMutation = useMutation({
    mutationFn: (formData: FormData) => putUserUpdate(id || '', formData, true),
    onSuccess: (data) => {
      notify.success({
        message: 'Update berhasil',
        description: 'Data user berhasil diperbaharui',
      })
      navigate(all_routes.adminUser)
    },
    onError: (err) => {
      notify.error({
        message: 'Update user gagal',
        description: err?.message || 'Terjadi kesalahan.',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id, true),
    onSuccess: () => {
      notify.success({
        message: 'Delete berhasil',
        description: 'Data user berhasil dihapus',
      })
      navigate(all_routes.adminUser)
    },
    onError: (err) => {
      notify.error({
        message: 'Delete user gagal',
        description: err?.message || 'Terjadi kesalahan.',
      })
    },
  })

  const onSubmit = (data: any) => {
    const formData = new FormData()

    for (const key in data) {
      if (key === 'profile_picture') {
        const file = data?.profile_picture?.[0]
        if (file) {
          // Handle new file upload
          formData.append('profile_picture', file)
        } else if (
          data?.profile_picture &&
          typeof data.profile_picture === 'string'
        ) {
          // Keep existing profile picture when updating without changing it
          formData.append('profile_picture', data.profile_picture)
        }
      } else if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key] as any)
      }
    }

    updateMutation.mutate(formData)
  }

  const profilePicture: any = watch('profile_picture')

  useEffect(() => {
    if (profilePicture?.[0]?.name) {
      const objectUrl = URL.createObjectURL(profilePicture[0])
      setPreview(objectUrl)
      return () => URL.revokeObjectURL(objectUrl) // cleanup
    }
  }, [profilePicture])

  return (
    <>
      <div className="content">
        <div className="container">
          <div className="row">
            <InstructorSidebar />

            <div className="col-lg-9">
              <Link to="/admin/user" className="d-flex align-items-center mb-3">
                <i className="isax isax-arrow-left me-1 fw-bold" />
                Back to List
              </Link>

              {responseQuery.isLoading ? (
                <Flex className="h-100" align="center" justify="center">
                  <Spin tip="Loading..." size="large" />
                </Flex>
              ) : (
                <>
                  <div className="card">
                    <div className="card-body">
                      <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        {/* Profile Photo */}
                        <div className="profile-upload-group mb-4">
                          <div className="d-flex align-items-center">
                            <div className="avatar flex-shrink-0 avatar-xxxl avatar-rounded border me-3">
                              <ImageWithBasePath
                                src={preview || '/assets/img/user/user-02.jpg'}
                                {...(!preview && {
                                  srcApi: userDetail?.profile_picture,
                                })}
                                alt="Img"
                                className="img-fluid"
                              />
                            </div>
                            <div className="profile-upload-head">
                              <h6>
                                <Link to="/student/profile">Profile Photo</Link>
                              </h6>
                              <p className="fs-14 mb-0">PNG or JPG</p>
                              <div className="new-employee-field">
                                <div className="d-flex align-items-center mt-2">
                                  <div className="image-upload position-relative mb-0 me-2">
                                    <input
                                      type="file"
                                      {...register('profile_picture')}
                                    />
                                    <Link
                                      to="#"
                                      className="btn bg-gray-100 btn-sm rounded-pill image-uploads"
                                    >
                                      Upload
                                    </Link>
                                  </div>
                                  {/* <div className="img-delete">
                                    <Link
                                      to="#"
                                      className="btn btn-secondary btn-sm rounded-pill"
                                    >
                                      Delete
                                    </Link>
                                  </div> */}
                                </div>
                              </div>
                              {errors.profile_picture && (
                                <p className="text-danger mt-1">
                                  {errors.profile_picture.message}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Personal Details Header */}
                        <div className="edit-profile-info mb-3">
                          <h5 className="mb-1">Personal Details</h5>
                          <p>Edit your personal information</p>
                        </div>

                        <div className="row">
                          {userFields.map(([name, config]) => {
                            if (name === 'profile_picture') return null
                            if (name === 'role') {
                              return (
                                <div key={name} className="col-md-6 mb-3">
                                  <label className="form-label">
                                    {config.label}{' '}
                                    {config.required && (
                                      <span className="text-danger">*</span>
                                    )}
                                  </label>
                                  <Select
                                    defaultValue={userDetail?.role}
                                    className="select d-flex"
                                    placeholder={config.placeholder}
                                    style={{ width: '100%' }}
                                    options={Role_Option}
                                    onChange={(value) => setValue(name, value)}
                                  />

                                  {errors[name] && (
                                    <p className="text-danger mt-1">
                                      {errors[name]?.message as string}
                                    </p>
                                  )}
                                </div>
                              )
                            }

                            if (name === 'phone_number') {
                              return (
                                <div key={name} className="col-md-6 mb-3">
                                  <label className="form-label">
                                    {config.label}{' '}
                                    {config.required && (
                                      <span className="text-danger">*</span>
                                    )}
                                  </label>
                                  <input
                                    type={config.type}
                                    {...register(name as any)}
                                    className={`form-control ${
                                      errors[name] ? 'is-invalid' : ''
                                    }`}
                                    placeholder={config.placeholder}
                                  />
                                  {errors[name] && (
                                    <p className="text-danger mt-1">
                                      {errors[name]?.message as string}
                                    </p>
                                  )}
                                </div>
                              )
                            }

                            if (config.type === 'textarea') {
                              return (
                                <div key={name} className="col-md-12 mb-3">
                                  <label className="form-label">
                                    {config.label}{' '}
                                    {config.required && (
                                      <span className="text-danger">*</span>
                                    )}
                                  </label>
                                  <textarea
                                    rows={4}
                                    {...register(name as any)}
                                    className={`form-control ${
                                      errors[name] ? 'is-invalid' : ''
                                    }`}
                                    placeholder={config.placeholder}
                                  />
                                  {errors[name] && (
                                    <p className="text-danger mt-1">
                                      {errors[name]?.message as string}
                                    </p>
                                  )}
                                </div>
                              )
                            }

                            if (config.type === 'password') {
                              return (
                                <div key={name} className="col-md-6 mb-3">
                                  <label className="form-label">
                                    {config.label}{' '}
                                    {config.required && (
                                      <span className="text-danger">*</span>
                                    )}
                                  </label>
                                  <input
                                    type="password"
                                    {...register(name as any)}
                                    className={`form-control ${
                                      errors[name] ? 'is-invalid' : ''
                                    }`}
                                    placeholder={config.placeholder}
                                  />
                                  {errors[name] && (
                                    <p className="text-danger mt-1">
                                      {errors[name]?.message as string}
                                    </p>
                                  )}
                                </div>
                              )
                            }

                            // default input text, email, etc
                            return (
                              <div key={name} className="col-md-6 mb-3">
                                <label className="form-label">
                                  {config.label}{' '}
                                  {config.required && (
                                    <span className="text-danger">*</span>
                                  )}
                                </label>
                                <input
                                  type={config.type}
                                  {...register(name as any)}
                                  className={`form-control ${
                                    errors[name] ? 'is-invalid' : ''
                                  }`}
                                  placeholder={config.placeholder}
                                />
                                {errors[name] && (
                                  <p className="text-danger mt-1">
                                    {errors[name]?.message as string}
                                  </p>
                                )}
                              </div>
                            )
                          })}
                        </div>
                        <div className="col-md-12">
                          <button
                            disabled={updateMutation.isPending}
                            type="submit"
                            className="btn btn-secondary rounded-pill"
                          >
                            Update Profile
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>

                  <div className="card mb-0">
                    <div className="card-body">
                      <h5 className="fs-18 mb-3">Delete Account</h5>
                      <h6 className="mb-1">
                        Are you sure you want to delete your account?
                      </h6>
                      <p className="mb-3">
                        Refers to the action of permanently removing a user's
                        account and associated data from a system, service and
                        platform.
                      </p>
                      <button
                        disabled={deleteMutation.isPending}
                        className="btn btn-secondary"
                        data-bs-toggle="modal"
                        data-bs-target="#delete_account"
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <SettingsModal onDeleteUser={() => deleteMutation.mutate(id || '')} />
    </>
  )
}

export default UserEdit
